import MuleBadge from '../components/MuleBadge';
/**
 * Dynamic Columns Utility
 *
 * Generates TanStack Table column definitions from field definitions.
 * Used to enable dynamic column selection in dispatch dashboard tables.
 *
 * Configuration is fetched from the backend /dispatch-dashboard/column-config endpoint
 * to ensure frontend and backend stay in sync.
 *
 * See: DISPATCH_DASHBOARD_DYNAMIC_COLUMNS_PLAN.md
 */

import { flexRender, ColumnDef } from '@tanstack/react-table';
import { FieldDefinition } from '../contexts/FieldDefinitionsContext';
import { Tooltip } from '../components/ui/Tooltip';
import { useQuoteDrawer } from '../contexts/QuoteDrawerContext';

// =============================================================================
// COLUMN CONFIGURATION (loaded from backend)
// =============================================================================

/**
 * Configuration structure returned by /dispatch-dashboard/column-config
 */
export interface ColumnConfig {
  field_name_mapping: Record<string, string>;
  base_bucket_fields: string[];
  bucket_priority_fields: Record<string, string[]>;
  core_fields: string[];
}

// Default fallback config (used until backend config is loaded)
// These should match the backend defaults to avoid any flash of different columns
const DEFAULT_CONFIG: ColumnConfig = {
  field_name_mapping: {
    customername: 'customer_name',
    billtoname: 'billto_name',
    dispatchername: 'dispatcher_name',
    dispatcherusername: 'dispatcher_username',
    revenueregion: 'region',
    d_arrivaldatetime: 'deliverydate',
    currentlocationname: 'currentlocation',
  },
  base_bucket_fields: [
    'pronumber', 'movetype', 'dispatchername', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'origname', 'o_origapptdate', 'destname', 'd_destapptdate', 't_destname',
    't_destapptdate', 'cutoffdatetime'
  ],
  bucket_priority_fields: {
    'per-diem-not-dispatched': ['perdiemstartdate', 'lastfreedaydate'],
    'cutoff-not-dispatched': ['cutoffdatetime'],
    'cutoff-not-terminated': ['cutoffdatetime'],
    'drop-released-not-dispatched': ['dropreleasedatetime', 'streetturnopportunity'],
    'past-due': ['deliveryoption1date'],
    'lfd-today-or-past': ['lastfreedaydate', 'perdiemstartdate', 'perdiemapproval', 'perdiemnotes'],
    'scheduled-not-dispatched': ['d_legmiles'],
  },
  core_fields: ['pronumber', 'container', 'movetype', 'size', 'customercode', 'customername', 'dispatcher', 'dispatchername'],
};

// Bucket-specific base fields that completely override the default base_bucket_fields
// These buckets need entirely different field sets, not just priority fields prepended
const BUCKET_SPECIFIC_BASE_FIELDS: Record<string, string[]> = {
  'scheduled-not-dispatched': [
    'pronumber', 'movetype', 'dispatchername', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'd_legmiles', 'leg_legtype', 'leg_origname', 'leg_origapptdate', 'leg_legaction2', 'leg_destname', 'leg_destapptdate',
    'cutoffdatetime'
  ],
  'dispatched-driver-oos': [
    'pronumber', 'movetype', 'dispatchername', 'oos_driver', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'origname', 'o_origapptdate', 'destname', 'd_destapptdate', 't_destname', 't_destapptdate',
    'cutoffdatetime'
  ],
  'dispatched-not-yet-arrived': [
    'pronumber', 'movetype', 'dispatchername', 'd_driver', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'origname', 'o_origapptdate', 'destname', 'd_destapptdate', 't_destname', 't_destapptdate',
    'cutoffdatetime'
  ],
  'dispatches-not-accepted': [
    'dispatched_driver', 'leg_origname', 'leg_destname', 'pronumber', 'dispatched_datetime', 'leg_destapptdate'
  ],
  'trace-terminate-no-driver': [
    'pronumber', 'keyaccount', 'container', 'movetype', 'scac', 'size', 'equiptype',
    'd_driver', 'd_dspdriverdate', 'destname', 'origname', 't_destname',
    'chassisprovider', 'chassistype', 'outgatedatetime', 'perdiemstartdate',
    'cutoffdatetime', 'traceterminatedatetime'
  ],
  'pod-bol-mr-pl-eir-required': [
    'pronumber', 'keyaccount', 'container', 'movetype', 'scac', 'size', 'equiptype',
    'd_driver', 'destname', 't_driver', 't_destname',
    'chassisprovider', 'chassistype', 'outgatedatetime', 'perdiemstartdate', 'cutoffdatetime'
  ],
  'per-diem-not-dispatched': [
    'perdiemstartdate', 'lastfreedaydate', 'perdiemdays', 'perdiemapproval', 'perdiemnotes',
    'pronumber', 'movetype', 'dispatchername', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'origname', 'o_origapptdate', 'destname', 'd_destapptdate', 't_destname', 't_destapptdate',
    'cutoffdatetime'
  ],
  'past-due': [
    'deliveryoption1date', 'perdiemdays', 'perdiemapproval', 'perdiemnotes',
    'pronumber', 'movetype', 'dispatchername', 'container', 'size', 'equiptype',
    'scac', 'chassisid', 'chassistype', 'billtoname', 'customername', 'bol', 'po',
    'origname', 'o_origapptdate', 'destname', 'd_destapptdate', 't_destname', 't_destapptdate',
    'cutoffdatetime'
  ],
  'trouble-tickets-dispatcher': [
    'pronumber', 'keyaccount', 'movetype', 'bol', 'container', 'rail',
    'dispatcher', 'dispatchername',
    'tt_recno', 'tt_createdby', 'tt_datetime', 'tt_status',
    'tt_category', 'tt_issue', 'tt_resolve',
    'tt_driverticket', 'tt_assignedto',
  ],
  'mismatched-drivers': [
    'pronumber', 'legnum', 'next_legnum', 'destapptdate', 'next_destapptdate', 'driver', 'next_driver',
  ],
  'invalid-container-numbers': [
    'pronumber', 'container', 'dispatcher', 'enteredby',
  ],
  'missing-waiting-time': [
    'pronumber', 'keyaccount', 'container', 'movetype', 'scac',
    'dpdriver', 'dpeventdate', 'dpintime', 'dpouttime', 'dplocation', 'dpdescription',
  ],
};

// Cached config (set by setColumnConfig or loadColumnConfig)
let _columnConfig: ColumnConfig = DEFAULT_CONFIG;

/**
 * Set the column configuration (called after fetching from backend).
 * This allows the entire app to use the backend-provided configuration.
 */
export function setColumnConfig(config: ColumnConfig): void {
  _columnConfig = config;
}

/**
 * Get the current column configuration.
 */
export function getColumnConfig(): ColumnConfig {
  return _columnConfig;
}

// =============================================================================
// BUCKET FIELD DEFINITIONS
// =============================================================================

/**
 * Get base bucket fields from configuration.
 * @deprecated Use getBucketFields(bucketKey) instead for full bucket fields.
 */
export function getBaseBucketFields(): string[] {
  return _columnConfig.base_bucket_fields;
}

// Export for backward compatibility
export const BASE_BUCKET_FIELDS = DEFAULT_CONFIG.base_bucket_fields;
export const BUCKET_PRIORITY_FIELDS = DEFAULT_CONFIG.bucket_priority_fields;

/**
 * Get the complete list of fields for a bucket.
 * Returns bucket-specific base fields if defined, otherwise priority fields + default base fields.
 */
export function getBucketFields(bucketKey: string): string[] {
  // Check if this bucket has completely custom base fields
  if (BUCKET_SPECIFIC_BASE_FIELDS[bucketKey]) {
    return BUCKET_SPECIFIC_BASE_FIELDS[bucketKey];
  }
  // Otherwise use priority fields + default base fields
  const priorityFields = _columnConfig.bucket_priority_fields[bucketKey] || [];
  return [...new Set([...priorityFields, ..._columnConfig.base_bucket_fields])];
}

// =============================================================================
// FIELD NAME MAPPING
// =============================================================================

/**
 * Get the API field name for a database field name.
 * Some fields are renamed for the frontend API.
 */
export function getApiFieldName(dbFieldName: string): string {
  return _columnConfig.field_name_mapping[dbFieldName] || dbFieldName;
}

/**
 * Get the database field name from an API field name.
 */
export function getDbFieldName(apiFieldName: string): string {
  const reverseMapping = Object.entries(_columnConfig.field_name_mapping).find(
    ([_, api]) => api === apiFieldName
  );
  return reverseMapping ? reverseMapping[0] : apiFieldName;
}

/**
 * Format a date value as MM/DD (or MM/DD/YY if not current year)
 */
export function formatDate(value: string | null | undefined): string {
  if (!value || value === '0000-00-00' || value === '0000-00-00 00:00:00') {
    return '';
  }

  try {
    // Handle both ISO and space-separated formats
    let dateStr = value;
    if (value.includes(' ')) {
      dateStr = value.split(' ')[0];
    } else if (value.includes('T')) {
      dateStr = value.split('T')[0];
    }

    const dateValue = new Date(dateStr + 'T00:00:00');
    if (isNaN(dateValue.getTime())) return value;

    const currentYear = new Date().getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');

    // Show 2-digit year if not current year
    if (dateValue.getFullYear() !== currentYear) {
      const year = String(dateValue.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    }

    return `${month}/${day}`;
  } catch {
    return value;
  }
}

/**
 * Format a datetime value as MM/DD HH:MM (or MM/DD/YY HH:MM if not current year)
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') {
    return '';
  }

  try {
    let dateObj: Date;
    if (value.includes('T')) {
      dateObj = new Date(value);
    } else if (value.includes(' ')) {
      const [datePart, timePart] = value.split(' ');
      dateObj = new Date(`${datePart}T${timePart}`);
    } else {
      dateObj = new Date(value + 'T00:00:00');
    }

    if (isNaN(dateObj.getTime())) return value;

    const currentYear = new Date().getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    // Show 2-digit year if not current year
    if (dateObj.getFullYear() !== currentYear) {
      const year = String(dateObj.getFullYear()).slice(-2);
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    }

    return `${month}/${day} ${hours}:${minutes}`;
  } catch {
    return value;
  }
}

/**
 * Format a number value with locale formatting
 */
function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  return num.toLocaleString();
}

/**
 * Format a boolean value as Y/N (matches the Y/N convention used across the
 * rest of the system). The filter UI still labels the dropdown Yes/No.
 */
function formatBoolean(value: boolean | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    return ['true', 'yes', 'y', '1'].includes(value.toLowerCase()) ? 'Y' : 'N';
  }

  return value ? 'Y' : 'N';
}

/**
 * Field definitions whose value is a "quote-revision" reference, e.g. "96547-1".
 *
 * Kept as a set rather than a single name because the shape, not the field, is what makes
 * a cell clickable: any field whose SQL produces a quote-revision pair belongs here and
 * becomes a link everywhere it is picked — Search Results, an LC dashboard, a pro grid.
 */
export const QUOTE_REF_FIELDS = new Set(['mainv_quotenumber']);

/** "96547-1" → [96547, 1]. Anything else → null, including a bare number: without a
 *  revision there is no revision row to open, so it must not become a link. */
export function parseQuoteRef(value: unknown): [number, number] | null {
  const m = /^\s*(\d+)-(\d+)\s*$/.exec(String(value ?? ''));
  if (!m) return null;
  const quote = Number(m[1]);
  const revision = Number(m[2]);
  return quote > 0 && revision > 0 ? [quote, revision] : null;
}

/**
 * A quote reference as a link into the shared quote drawer.
 *
 * A component rather than inline JSX because it needs the drawer hook. With no provider
 * mounted it renders the reference as plain text — the column generator is called from
 * tests and from surfaces that do not wrap QuoteDrawerProvider, and a quote number that
 * is simply not clickable there is the right outcome.
 */
const QuoteRefCell: React.FC<{ value: unknown }> = ({ value }) => {
  const { openQuote, available } = useQuoteDrawer();
  const ref = parseQuoteRef(value);
  const text = String(value ?? '');
  if (!ref || !available) return <>{text}</>;
  return (
    <button
      type="button"
      onClick={(e) => {
        // Grids put their own handler on the row (open the pro, select it). A quote link
        // that also triggered that would open two things from one click.
        e.stopPropagation();
        openQuote(ref[0], ref[1]);
      }}
      className="text-accent-text hover:underline"
      title={`Open quote ${text} in a drawer`}
    >
      {text}
    </button>
  );
};

/**
 * Generate a TanStack Table column definition from a field definition.
 *
 * This creates a basic column definition with type-appropriate rendering.
 * For columns that need special rendering (like colored date badges),
 * use custom column definitions instead.
 */
export function generateColumnDef(field: FieldDefinition): ColumnDef<any> {
  const apiFieldName = getApiFieldName(field.fieldname);

  // The field's Field Manager `aggregate_default` rides along on meta so the
  // TanTable totals bar gets its starting calculation from the definition we are
  // holding — never by matching a column id back to a field name, which is
  // ambiguous (see docs/plans/2026-08-27-tantable-aggregation-footer-design.md).
  const aggDefault = (field as any).aggregate_default || undefined;
  const baseMeta = aggDefault ? { aggDefault } : undefined;

  const baseDef: ColumnDef<any> = {
    accessorKey: apiFieldName,
    header: field.abbreviation || field.displayname,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: 'arrIncludes',
    ...(baseMeta ? { meta: baseMeta } : {}),
  };

  if (QUOTE_REF_FIELDS.has(field.fieldname)) {
    return {
      ...baseDef,
      cell: (info) => <QuoteRefCell value={info.getValue()} />,
      // Lexical order puts "9999-1" after "10000-1". Sort on the numbers behind the
      // string so the column orders the way a quote number reads.
      sortingFn: (a, b, id) => {
        const pa = parseQuoteRef(a.getValue(id)) ?? [0, 0];
        const pb = parseQuoteRef(b.getValue(id)) ?? [0, 0];
        return pa[0] - pb[0] || pa[1] - pb[1];
      },
      // Export the reference itself, not the rendered button.
      meta: { ...baseMeta, exportValue: (row: { original: Record<string, unknown> }) => String(row.original[apiFieldName] ?? '') },
    };
  }

  if (['driver', 'next_driver', 'oos_driver', 'd_driver', 't_driver', 'dispatched_driver', 'dpdriver'].includes(apiFieldName)) {
    return {
      ...baseDef,
      meta: {
        ...baseMeta,
        exportValue: (row: { original: Record<string, unknown> }) => `${row.original[apiFieldName] || ''}${row.original[`${apiFieldName}IsMule`] ? ' (Mule)' : ''}`,
      },
      cell: ({ row, getValue }) => <span>{String(getValue() || '')}<MuleBadge isMule={row.original[`${apiFieldName}IsMule`]} /></span>,
    };
  }

  // Type-driven cell rendering
  switch (field.type) {
    case 'date':
      return {
        ...baseDef,
        cell: (info) => formatDate(info.getValue() as string),
      };

    case 'datetime':
      return {
        ...baseDef,
        cell: (info) => formatDateTime(info.getValue() as string),
      };

    case 'boolean':
      return {
        ...baseDef,
        cell: (info) => formatBoolean(info.getValue() as boolean | string | null | undefined),
      };

    case 'number':
    case 'decimal':
      return {
        ...baseDef,
        cell: (info) => {
          const val = info.getValue();
          return formatNumber(val as number | string);
        },
        // `numeric`: the Excel export writes these as numbers even when the value
        // arrives as a string — a `-> Dict` route serializes Decimal as "395.00".
        meta: { align: 'right', numeric: true, ...baseMeta },
      };

    case 'text':
      return {
        ...baseDef,
        cell: (info) => {
          const val = info.getValue() as string;
          // Truncate long text
          if (val && val.length > 50) {
            return (
              <span title={val}>{val.substring(0, 50)}...</span>
            );
          }
          return val || '';
        },
      };

    default: // 'string'
      return {
        ...baseDef,
        cell: (info) => info.getValue() || '',
      };
  }
}

/** Preserve a custom driver cell while adding its type label and export text. */
export function decorateMuleDriverColumn<T extends object>(column: ColumnDef<T>, field: string): ColumnDef<T> {
  if (!['driver', 'next_driver', 'oos_driver', 'd_driver', 't_driver', 'dispatched_driver', 'dpdriver'].includes(field)) return column;
  return {
    ...column,
    meta: {
      ...column.meta,
      exportValue: (row: { original: T }) => {
        const data = row.original as Record<string, unknown>;
        return `${data[field] || ''}${data[`${field}IsMule`] ? ' (Mule)' : ''}`;
      },
    },
    cell: (context) => <>{column.cell ? flexRender(column.cell, context) : String(context.getValue() || '')}<MuleBadge isMule={!!(context.row.original as Record<string, unknown>)[`${field}IsMule`]} /></>,
  };
}

/**
 * Generate column definitions for a list of field definitions.
 *
 * @param fields - Array of field definitions from /available-fields endpoint
 * @param customColumns - Optional map of custom column definitions to use instead of generated ones
 * @returns Array of TanStack Table column definitions
 */
export function generateColumnDefs(
  fields: FieldDefinition[],
  customColumns?: Map<string, ColumnDef<any>>
): ColumnDef<any>[] {
  return fields.map((field) => {
    const apiFieldName = getApiFieldName(field.fieldname);

    // Use custom column definition if provided
    if (customColumns?.has(apiFieldName)) {
      const custom = customColumns.get(apiFieldName)!;
      return decorateMuleDriverColumn(custom, apiFieldName);
    }

    // Otherwise generate from field definition
    return generateColumnDef(field);
  });
}

// =============================================================================
// LOCATION ALIAS COLUMNS (shared by the Dispatch + LC dashboards)
// =============================================================================

/**
 * Location display columns show the cyalias (alias-or-lname); each carries a
 * hidden "{field}_full" companion with the raw full name (added to the row by
 * the backend). Map a display field → its full-name row key. Shared so location
 * columns hover/search consistently across dashboards.
 */
export const LOCATION_FULLNAME_FIELDS: Record<string, string> = {
  origname: 'origname_full',
  destname: 'destname_full',
  leg_origname: 'leg_origname_full',
  leg_destname: 'leg_destname_full',
  t_destname: 't_destname_full',
};

/** Hover text: "ALIAS - Full Name" when an alias is shown (display differs from
 *  the full name), else just the full name. */
export function locationHoverText(display: string, full: string): string {
  const d = (display || '').trim();
  const f = (full || '').trim();
  if (f && d && f !== d) return `${d} - ${f}`;
  return f || d;
}

/**
 * If `fieldname` is a location display column, return `col` with a hover tooltip
 * ("ALIAS - Full Name") sourced from the row's hidden {field}_full companion;
 * otherwise return `col` unchanged. Keeps the accessor/header/sort/filter on the
 * displayed alias; `disableTruncationTooltip` stops TanTable's own truncation
 * tooltip from competing with this one.
 */
export function decorateLocationColumn(col: ColumnDef<any>, fieldname: string): ColumnDef<any> {
  const fullKey = LOCATION_FULLNAME_FIELDS[fieldname];
  if (!fullKey) return col;
  return {
    ...col,
    meta: { ...(col.meta as object), disableTruncationTooltip: true },
    cell: ({ row, getValue }) => {
      const display = (getValue() as string) || '';
      if (!display) return '';
      const full = (row.original?.[fullKey] as string) || '';
      return (
        <Tooltip content={locationHoverText(display, full)} placement="top">
          <span className="truncate">{display}</span>
        </Tooltip>
      );
    },
  };
}

/**
 * Filter options available for each field type.
 * Used by the filter builder UI.
 */
export type FilterOption =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'between'
  | 'isEmpty'
  | 'isNotEmpty';

/**
 * Get available filter options for a field type.
 */
export function getFilterOptionsForType(fieldType: FieldDefinition['type']): FilterOption[] {
  switch (fieldType) {
    case 'date':
    case 'datetime':
      return ['equals', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'];

    case 'number':
    case 'decimal':
      return ['equals', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'];

    case 'boolean':
      return ['equals', 'isEmpty', 'isNotEmpty'];

    default: // string, text
      return ['equals', 'notEquals', 'contains', 'startsWith', 'endsWith', 'isEmpty', 'isNotEmpty'];
  }
}

/**
 * Sort field definitions alphabetically by display name.
 * Used for the column picker typeahead UI.
 */
export function sortFieldsByDisplayName(fields: FieldDefinition[]): FieldDefinition[] {
  return [...fields].sort((a, b) =>
    a.displayname.localeCompare(b.displayname)
  );
}

/**
 * Filter field definitions to only include those for a specific table.
 */
export function filterFieldsByTable(
  fields: FieldDefinition[],
  tablename: string
): FieldDefinition[] {
  return fields.filter((f) => f.tablename === tablename);
}

// =============================================================================
// FILTER OPERATOR UTILITIES
// =============================================================================

export type FieldType = 'string' | 'number' | 'date' | 'datetime' | 'time' | 'boolean' | 'decimal' | 'text' | 'nullable_boolean';

/** Operators available per field type - used by filter builders */
export const OPERATORS_BY_TYPE: Record<FieldType, string[]> = {
  string: ['Equal to', 'Not equal to', 'Contains', 'Does not contain', 'Starts with', 'Does not start with', 'Ends with', 'Does not end with', 'Is empty', 'Is not empty'],
  text: ['Equal to', 'Not equal to', 'Contains', 'Does not contain', 'Starts with', 'Does not start with', 'Ends with', 'Does not end with', 'Is empty', 'Is not empty'],
  number: ['Equal to', 'Not equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to', 'Between', 'Is empty', 'Is not empty'],
  decimal: ['Equal to', 'Not equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to', 'Between', 'Is empty', 'Is not empty'],
  date: ['Equal to', 'Not equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to', 'Between', 'Blank or after', 'Blank or before', 'Is empty', 'Is not empty'],
  datetime: ['Equal to', 'Not equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to', 'Between', 'Blank or after', 'Blank or before', 'Is empty', 'Is not empty'],
  time: ['Equal to', 'Not equal to', 'Greater than', 'Greater than or equal to', 'Less than', 'Less than or equal to', 'Between', 'Blank or after', 'Blank or before', 'Is empty', 'Is not empty'],
  boolean: ['Equal to', 'Is empty', 'Is not empty'],
  nullable_boolean: ['Equal to', 'Is empty', 'Is not empty'],
};

/** Fallback operators when field type is unknown */
export const DEFAULT_OPERATORS = [
  'Equal to',
  'Not equal to',
  'Between',
  'Contains',
  'Does not contain',
  'Greater than',
  'Less than',
  'Starts with',
  'Does not start with',
  'Ends with',
  'Does not end with',
  'Is empty',
  'Is not empty',
];

/** Operators for enum fields (fields with enum_options) */
const ENUM_OPERATORS = ['Equal to', 'Not equal to', 'Is empty', 'Is not empty'];

/** Get operators available for a field based on its type */
export function getOperatorsForField(fieldName: string, fieldDefinitions?: FieldDefinition[]): string[] {
  if (!fieldDefinitions || fieldDefinitions.length === 0) {
    return DEFAULT_OPERATORS;
  }

  const fieldDef = fieldDefinitions.find(f => f.fieldname === fieldName);
  if (!fieldDef) {
    return DEFAULT_OPERATORS;
  }

  // Enum fields get limited operators (no contains, starts with, etc.)
  if (fieldDef.enum_options && fieldDef.enum_options.length > 0) {
    return ENUM_OPERATORS;
  }

  return OPERATORS_BY_TYPE[fieldDef.type as FieldType] || DEFAULT_OPERATORS;
}

/** Get the type for a field */
export function getFieldTypeFromDefinitions(fieldName: string, fieldDefinitions?: FieldDefinition[]): FieldType | null {
  if (!fieldDefinitions || fieldDefinitions.length === 0) {
    return null;
  }

  const fieldDef = fieldDefinitions.find(f => f.fieldname === fieldName);
  return fieldDef?.type as FieldType || null;
}

/** Get placeholder text based on field type and operator */
export function getFilterPlaceholder(fieldType: FieldType | null, compare: string): string {
  // Operators that don't need a value
  if (compare === 'Is empty' || compare === 'Is not empty') {
    return '';
  }

  // Between operator
  if (compare === 'Between') {
    if (fieldType === 'date') return 'MM/DD/YYYY to MM/DD/YYYY';
    if (fieldType === 'datetime') return 'MM/DD/YYYY HH:MM to MM/DD/YYYY HH:MM';
    if (fieldType === 'time') return 'HH:MM to HH:MM';
    if (fieldType === 'number' || fieldType === 'decimal') return '0 to 100';
    return 'start to end';
  }

  // Multi-value hint for equals/not equals
  if (compare === 'Equal to' || compare === 'Not equal to') {
    if (fieldType === 'boolean' || fieldType === 'nullable_boolean') {
      return 'Yes or No';
    }
    return 'value1, value2, ...';
  }

  // LIKE operators support multi-value
  if (compare === 'Contains' || compare === 'Does not contain' ||
      compare === 'Starts with' || compare === 'Does not start with' ||
      compare === 'Ends with' || compare === 'Does not end with') {
    return 'value1, value2, ...';
  }

  // Type-specific placeholders
  if (fieldType === 'date') return 'MM/DD/YYYY';
  if (fieldType === 'datetime') return 'MM/DD/YYYY HH:MM';
  if (fieldType === 'time') return 'HH:MM';
  if (fieldType === 'number' || fieldType === 'decimal') return 'Enter number...';
  if (fieldType === 'boolean' || fieldType === 'nullable_boolean') return 'Yes or No';

  return 'Enter value...';
}

// =============================================================================
// DYNAMIC DATE UTILITIES
// =============================================================================

/**
 * Configuration for a dynamic date expression.
 * Used for UI state in the DynamicDatePicker component.
 */
export interface DynamicDateConfig {
  base: 'today' | 'now' | 'start_of_week' | 'start_of_month' | 'end_of_month' | 'start_of_last_month' | 'end_of_last_month' | 'start_of_quarter' | 'start_of_year';
  offset?: number;        // Positive = future, negative = past
  unit?: DynamicDateUnit;
}

/** minutes/hours only valid with base 'now'; business_days only with 'today'. */
export type DynamicDateUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'business_days';

/**
 * Preset date range for the Between operator.
 * Provides common date ranges like "Last 7 Days", "This Month", etc.
 */
export interface DateRangePreset {
  label: string;
  start: string;  // Dynamic date expression, e.g., "@today-7d"
  end: string;    // Dynamic date expression, e.g., "@today"
}

/**
 * Available date range presets for the Between operator.
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { label: 'Last 7 Days', start: '@today-7d', end: '@today' },
  { label: 'Last 30 Days', start: '@today-30d', end: '@today' },
  { label: 'Last 90 Days', start: '@today-90d', end: '@today' },
  { label: 'This Week', start: '@start_of_week', end: '@today' },
  { label: 'This Month', start: '@start_of_month', end: '@today' },
  // Unlike the "This ..." ranges, which run up to today, Last Month is a CLOSED
  // range: the 1st through the last day of the previous month.
  { label: 'Last Month', start: '@start_of_last_month', end: '@end_of_last_month' },
  { label: 'This Quarter', start: '@start_of_quarter', end: '@today' },
  { label: 'This Year', start: '@start_of_year', end: '@today' },
  { label: 'Next 7 Days', start: '@today', end: '@today+7d' },
  { label: 'Next 30 Days', start: '@today', end: '@today+30d' },
];

/**
 * Check if a value is a dynamic date expression.
 * Dynamic date expressions start with '@' (e.g., @today, @today+7d).
 */
export function isDynamicDateValue(value: string): boolean {
  return typeof value === 'string' && value.startsWith('@');
}

/**
 * Parse a dynamic date expression into a DynamicDateConfig.
 *
 * @param expr - Dynamic date expression (e.g., "@today", "@today+7d", "@start_of_month")
 * @returns Parsed config or null if invalid
 */
export function parseDynamicDateExpression(expr: string): DynamicDateConfig | null {
  if (!expr || !expr.startsWith('@')) return null;

  const inner = expr.slice(1); // Remove @

  // Base expressions without offset
  const baseOnly: DynamicDateConfig['base'][] = [
    'today', 'now', 'start_of_week', 'start_of_month',
    'end_of_month', 'start_of_last_month', 'end_of_last_month',
    'start_of_quarter', 'start_of_year'
  ];

  if (baseOnly.includes(inner as DynamicDateConfig['base'])) {
    return { base: inner as DynamicDateConfig['base'] };
  }

  // Offset expressions: today+7d, now-30d, today+2w, today+1m, now+2h, now+30min,
  // today+1bd ('min' and 'bd' are tried before the single-char units so they
  // never parse as months/days + trailing text)
  const match = inner.match(/^(today|now)([+-])(\d+)(min|bd|[dwmh])$/);
  if (match) {
    const [, base, op, amount, unit] = match;
    // Minutes/hours only make sense on @now — on the date-only @today they'd
    // silently mean "midnight + N" (backend rejects the same combinations)
    if ((unit === 'h' || unit === 'min') && base !== 'now') return null;
    // Business days are whole-day steps, so they're @today-only too
    if (unit === 'bd' && base !== 'today') return null;
    const unitMap: Record<string, DynamicDateUnit> = {
      min: 'minutes',
      h: 'hours',
      d: 'days',
      w: 'weeks',
      m: 'months',
      bd: 'business_days',
    };
    const offset = op === '-' ? -parseInt(amount, 10) : parseInt(amount, 10);
    return {
      base: base as 'today' | 'now',
      offset,
      unit: unitMap[unit],
    };
  }

  return null;
}

/**
 * Format a DynamicDateConfig back to an expression string.
 *
 * @param config - The dynamic date configuration
 * @returns Expression string like "@today" or "@today+7d"
 */
export function formatDynamicDateExpression(config: DynamicDateConfig): string {
  if (!config.offset || config.offset === 0) {
    return `@${config.base}`;
  }

  // Only today and now support offsets
  if (config.base !== 'today' && config.base !== 'now') {
    return `@${config.base}`;
  }

  const unitMap: Record<string, string> = {
    minutes: 'min',
    hours: 'h',
    days: 'd',
    weeks: 'w',
    months: 'm',
    business_days: 'bd',
  };
  const op = config.offset > 0 ? '+' : '-';
  // Minutes/hours only valid with base 'now', business days only with 'today'
  // — fall back to days elsewhere
  const timeUnitOffBase =
    (config.unit === 'hours' || config.unit === 'minutes') && config.base !== 'now';
  const bizDaysOffBase = config.unit === 'business_days' && config.base !== 'today';
  const effectiveUnit = timeUnitOffBase || bizDaysOffBase ? 'days' : config.unit;
  const unit = unitMap[effectiveUnit || 'days'];
  return `@${config.base}${op}${Math.abs(config.offset)}${unit}`;
}

/**
 * Move `n` business days from `start` (negative = backwards), skipping
 * Saturday and Sunday. From a weekend, +1 lands on Monday and -1 on Friday.
 * Returns a new Date; the input is not mutated. Mirrors the server's
 * `_add_business_days` so the preview agrees with the query.
 */
export function addBusinessDays(start: Date, n: number): Date {
  const d = new Date(start);
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  while (remaining > 0) {
    d.setDate(d.getDate() + step);
    const day = d.getDay(); // Sun=0 … Sat=6
    if (day === 0 || day === 6) continue;
    remaining -= 1;
  }
  return d;
}

/**
 * Resolve a dynamic date expression to an actual Date for preview purposes.
 * The preview is calculated in the user's local timezone.
 *
 * @param expr - Dynamic date expression (e.g., "@today", "@today+7d")
 * @returns Resolved Date or null if invalid
 */
export function resolveDynamicDateForPreview(expr: string): Date | null {
  const config = parseDynamicDateExpression(expr);
  if (!config) return null;

  const now = new Date();
  let date = new Date(now);

  // Set base date (reset time to start of day for date-only expressions)
  switch (config.base) {
    case 'today':
      date.setHours(0, 0, 0, 0);
      break;
    case 'now':
      // Keep current time
      break;
    case 'start_of_week': {
      // Monday of current week (Monday = 1, Sunday = 0)
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day; // If Sunday, go back 6 days
      date.setDate(date.getDate() + diff);
      date.setHours(0, 0, 0, 0);
      break;
    }
    case 'start_of_month':
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
      break;
    // NOTE on the setDate(1) that precedes every setMonth() below: setMonth()
    // keeps the current day-of-month, so on the 31st it overflows into the month
    // after the one asked for (Jan 31 -> setMonth(1) -> "Feb 31" -> Mar 3). That
    // made these previews disagree with the SQL the server runs. Parking on the
    // 1st first makes every target month reachable.
    case 'end_of_month':
      date.setDate(1);
      date.setMonth(date.getMonth() + 1);
      date.setDate(0); // Last day of the month we started in
      date.setHours(0, 0, 0, 0);
      break;
    case 'start_of_last_month':
      date.setDate(1);
      date.setMonth(date.getMonth() - 1);
      date.setHours(0, 0, 0, 0);
      break;
    case 'end_of_last_month':
      // Day 0 of this month IS the last day of the previous one — no setMonth(),
      // so no day-of-month overflow to guard against.
      date.setDate(0);
      date.setHours(0, 0, 0, 0);
      break;
    case 'start_of_quarter': {
      const quarter = Math.floor(date.getMonth() / 3);
      date.setDate(1);
      date.setMonth(quarter * 3);
      date.setHours(0, 0, 0, 0);
      break;
    }
    case 'start_of_year':
      date.setDate(1);
      date.setMonth(0);
      date.setHours(0, 0, 0, 0);
      break;
  }

  // Apply offset
  if (config.offset && config.unit) {
    switch (config.unit) {
      case 'minutes':
        date = new Date(date.getTime() + config.offset * 60 * 1000);
        break;
      case 'hours':
        date = new Date(date.getTime() + config.offset * 3600 * 1000);
        break;
      case 'days':
        date.setDate(date.getDate() + config.offset);
        break;
      case 'weeks':
        date.setDate(date.getDate() + (config.offset * 7));
        break;
      case 'months':
        date.setMonth(date.getMonth() + config.offset);
        break;
      case 'business_days':
        date = addBusinessDays(date, config.offset);
        break;
    }
  }

  return date;
}

/**
 * Get a human-readable label for a dynamic date expression.
 *
 * @param expr - Dynamic date expression (e.g., "@today", "@today+7d")
 * @returns Human-readable label like "Today" or "Today + 7 Days"
 */
export function getDynamicDateDisplayLabel(expr: string): string {
  const config = parseDynamicDateExpression(expr);
  if (!config) return expr;

  const baseLabels: Record<string, string> = {
    today: 'Today',
    now: 'Now',
    start_of_week: 'Start of Week',
    start_of_month: 'Start of Month',
    end_of_month: 'End of Month',
    start_of_last_month: 'Start of Last Month',
    end_of_last_month: 'End of Last Month',
    start_of_quarter: 'Start of Quarter',
    start_of_year: 'Start of Year',
  };

  let label = baseLabels[config.base] || config.base;

  if (config.offset && config.offset !== 0) {
    const unitLabels: Record<string, string> = {
      minutes: 'Minute',
      hours: 'Hour',
      days: 'Day',
      weeks: 'Week',
      months: 'Month',
      business_days: 'Business Day',
    };
    const unit = unitLabels[config.unit || 'days'];
    const plural = Math.abs(config.offset) !== 1 ? 's' : '';
    const op = config.offset > 0 ? '+' : '-';
    label += ` ${op} ${Math.abs(config.offset)} ${unit}${plural}`;
  }

  return label;
}

/**
 * Render a stored filter value the way a person should read it.
 *
 * Relative dates are STORED as the tokens the server resolves — the active-filter
 * banner was showing "@start_of_last_month to @end_of_last_month" verbatim. A
 * range that matches a preset reads back as that preset's name; any other range
 * falls back to its two labels. Static values, and text that merely contains an
 * '@' (an email filter), pass through untouched.
 *
 * @param values - The condition's stored value
 * @returns Display text for the filter chips
 */
export function formatFilterValueForDisplay(values: string): string {
  const raw = (values ?? '').trim();
  if (!raw.includes('@')) return raw;

  if (raw.includes(' to ')) {
    const preset = findMatchingPreset(raw);
    if (preset) return preset.label;
    const [start, end] = raw.split(' to ').map((part) => part.trim());
    const label = (part: string) =>
      isDynamicDateValue(part) ? getDynamicDateDisplayLabel(part) : part;
    return `${label(start)} to ${label(end)}`;
  }

  // getDynamicDateDisplayLabel returns the input unchanged when it does not
  // parse, so a literal like "@gwii.com" is left alone.
  return isDynamicDateValue(raw) ? getDynamicDateDisplayLabel(raw) : raw;
}

/**
 * Reshape a condition's stored value when its operator changes.
 *
 * "Between" stores "<start> to <end>"; every other operator stores a single
 * value. Changing the operator used to leave the old shape behind, and the
 * server cannot use it either way: "@today" under Between has no " to ", so the
 * condition was silently dropped (the grid came back unfiltered), and
 * "@today-7d to @today" under a single-value operator is not a parseable
 * dynamic date, which failed the request outright.
 *
 * @param values - The condition's current value
 * @param nextCompare - The operator being switched to
 * @param prevCompare - The operator being switched from
 * @returns The value in the shape the new operator expects
 */
export function reconcileValueForOperator(
  values: string,
  nextCompare: string,
  prevCompare: string
): string {
  if (nextCompare === prevCompare) return values;

  const trimmed = values.trim();
  if (!trimmed) return values;
  const isRange = values.includes(' to ');

  // Single value -> Between: seed both ends from it so the picker opens on the
  // date the user already chose rather than resetting them to a default range.
  if (nextCompare === 'Between') {
    return isRange ? values : `${trimmed} to ${trimmed}`;
  }

  // Between -> single value: keep the start of the range.
  if (isRange && prevCompare === 'Between') {
    return values.split(' to ')[0].trim();
  }

  return values;
}

/**
 * Find a matching preset for a Between range value.
 *
 * @param value - Between value like "@today-7d to @today"
 * @returns Matching preset or null if no match
 */
export function findMatchingPreset(value: string): DateRangePreset | null {
  if (!value || !value.includes(' to ')) return null;

  const [start, end] = value.split(' to ').map(s => s.trim());

  return DATE_RANGE_PRESETS.find(
    preset => preset.start === start && preset.end === end
  ) || null;
}

// =============================================================================
// DATE VALUE PARSING UTILITIES
// =============================================================================

/**
 * DateTimeValue interface for range picker values.
 * Re-exported here to avoid circular dependencies with DateTimePicker.
 */
export interface BetweenDateValue {
  startDate?: Date;
  endDate?: Date;
}

/**
 * Parse a stored filter date value into a Date in the user's LOCAL timezone.
 *
 * `new Date('2026-10-01')` is specified to read a date-ONLY string as UTC, so in
 * US timezones it lands the evening before — a range picked as Oct 1 to Oct 10
 * read back as 09/30 to 10/09, and re-applying it saved that shifted range. (A
 * string WITH a time, '2026-10-01T09:00', is already parsed as local, so only the
 * date-only form drifted.) Splitting the parts out keeps everything local, which
 * is what formatBetweenValue writes with its local getters — so a value now
 * round-trips unchanged.
 *
 * @param value - 'YYYY-MM-DD', 'YYYY-MM-DDTHH:MM', or the SQL-style
 *                'YYYY-MM-DD HH:MM[:SS]' that saved filters can carry
 * @returns The Date, or null when the value is empty or unparseable
 */
export function parseLocalDateValue(value: string): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(trimmed);
  if (match) {
    const [, year, month, day, hours, minutes, seconds] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours ?? 0),
      Number(minutes ?? 0),
      Number(seconds ?? 0)
    );
  }

  // Anything else (a legacy format) falls back to the engine's parser.
  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Parse a "Between" value string into a date range object for the DateTimePicker.
 * Format: "YYYY-MM-DD to YYYY-MM-DD" or "YYYY-MM-DDTHH:MM to YYYY-MM-DDTHH:MM"
 *
 * @param value - The between value string to parse
 * @returns Parsed date range or null if invalid
 */
export function parseBetweenValue(value: string): BetweenDateValue | null {
  if (!value || !value.includes(' to ')) {
    return null;
  }
  const [startStr, endStr] = value.split(' to ');
  const startDate = parseLocalDateValue(startStr);
  const endDate = parseLocalDateValue(endStr);

  // A bound that was present but unparseable makes the whole range unusable.
  if (startStr.trim() && !startDate) return null;
  if (endStr.trim() && !endDate) return null;

  return {
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
  };
}

/**
 * Format a date range object from the DateTimePicker into the "Between" string format
 * expected by the backend: "YYYY-MM-DD to YYYY-MM-DD" or "YYYY-MM-DDTHH:MM to YYYY-MM-DDTHH:MM"
 *
 * @param value - The date range value to format
 * @param includeTime - Whether to include time in the output
 * @returns Formatted string like "2024-01-01 to 2024-12-31"
 */
export function formatBetweenValue(value: BetweenDateValue | null, includeTime: boolean): string {
  if (!value || !value.startDate || !value.endDate) {
    return '';
  }

  const formatDate = (date: Date): string => {
    // Format using local time getters to avoid UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (includeTime) {
      // Format as YYYY-MM-DDTHH:MM for datetime
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    // Format as YYYY-MM-DD for date only
    return `${year}-${month}-${day}`;
  };

  return `${formatDate(value.startDate)} to ${formatDate(value.endDate)}`;
}

/**
 * Serialize a Date's time-of-day to the zero-padded 24-hour "HH:MM" format
 * stored in time-typed fields (e.g., appt time columns).
 */
export function formatTimeFilterValue(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Parse an "HH:MM" filter value into a Date (today, at that time) for the
 * DateTimePicker. Returns null for empty or malformed values.
 */
export function parseTimeFilterValue(value: string): Date | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) return null;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}
